import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useCompanyProfile, useSaveCompanyProfile } from '../../hooks/useCompanyProfile';
import type { CompanyProfile, CompanyProfileInput } from '../../lib/companyProfileApi';
import { lookupAnafByCui } from '../../lib/anafApi';
import { CompanyLogoField } from './CompanyLogoField';
import { Select } from '../ui/Select';

// Settings > Company profile (SaaS hardening). The default firm's profile
// drives the brand name in the sidebar, invoice defaults and email templates.
// GET is open to any authenticated user; saving is ADMIN-only (system role),
// so the form renders read-only for everyone else.
//
// The field grid + ANAF lookup are extracted as `CompanyFormFields` so the
// multi-company Settings > Firme tab (CompaniesSettings) reuses the exact
// same form for add/edit modals.

export interface CompanyFormState {
  legalName: string;
  displayName: string;
  cui: string;
  regCom: string;
  address: string;
  city: string;
  county: string;
  country: string;
  iban: string;
  bank: string;
  email: string;
  phone: string;
  website: string;
  /** Square logo as a data URL; '' when the firm has none. */
  logoUrl: string;
  defaultCurrency: 'RON' | 'EUR' | 'USD';
  vatPayer: boolean;
}

export const EMPTY_COMPANY_FORM: CompanyFormState = {
  legalName: '',
  displayName: '',
  cui: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
  country: 'România',
  iban: '',
  bank: '',
  email: '',
  phone: '',
  website: '',
  logoUrl: '',
  defaultCurrency: 'RON',
  vatPayer: true,
};

/** Shape shared by the default-firm profile alias and the multi-company API. */
type CompanyLike = Pick<
  CompanyProfile,
  | 'legalName'
  | 'displayName'
  | 'cui'
  | 'regCom'
  | 'address'
  | 'city'
  | 'county'
  | 'country'
  | 'iban'
  | 'bank'
  | 'email'
  | 'phone'
  | 'website'
  | 'logoUrl'
  | 'defaultCurrency'
  | 'vatPayer'
>;

export function companyToForm(profile: CompanyLike | null): CompanyFormState {
  if (!profile) return EMPTY_COMPANY_FORM;
  return {
    legalName: profile.legalName,
    displayName: profile.displayName ?? '',
    cui: profile.cui ?? '',
    regCom: profile.regCom ?? '',
    address: profile.address ?? '',
    city: profile.city ?? '',
    county: profile.county ?? '',
    country: profile.country || 'România',
    iban: profile.iban ?? '',
    bank: profile.bank ?? '',
    email: profile.email ?? '',
    phone: profile.phone ?? '',
    website: profile.website ?? '',
    logoUrl: profile.logoUrl ?? '',
    defaultCurrency:
      profile.defaultCurrency === 'EUR' || profile.defaultCurrency === 'USD'
        ? profile.defaultCurrency
        : 'RON',
    vatPayer: profile.vatPayer,
  };
}

export function companyFormToInput(form: CompanyFormState): CompanyProfileInput {
  const opt = (v: string) => (v.trim() ? v.trim() : null);
  return {
    legalName: form.legalName.trim(),
    displayName: opt(form.displayName),
    cui: opt(form.cui),
    regCom: opt(form.regCom),
    address: opt(form.address),
    city: opt(form.city),
    county: opt(form.county),
    country: opt(form.country),
    iban: opt(form.iban),
    bank: opt(form.bank),
    email: opt(form.email),
    phone: opt(form.phone),
    website: opt(form.website),
    logoUrl: opt(form.logoUrl),
    defaultCurrency: form.defaultCurrency,
    vatPayer: form.vatPayer,
  };
}

const TEXT_FIELDS: { key: keyof CompanyFormState; label: string; type?: string; full?: boolean }[] =
  [
    { key: 'legalName', label: 'Nume legal *', full: true },
    { key: 'displayName', label: 'Nume afișat (brand)' },
    { key: 'regCom', label: 'Nr. Reg. Com.' },
    { key: 'address', label: 'Adresă', full: true },
    { key: 'city', label: 'Oraș' },
    { key: 'county', label: 'Județ' },
    { key: 'country', label: 'Țară' },
    { key: 'iban', label: 'IBAN' },
    { key: 'bank', label: 'Bancă' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'phone', label: 'Telefon' },
    { key: 'website', label: 'Website' },
  ];

/**
 * The reusable field grid (CUI + ANAF prefill, identity/contact fields,
 * currency, VAT flag). Purely controlled: state lives in the parent.
 * Used by CompanyProfileForm (default firm) and CompaniesSettings modals.
 */
export function CompanyFormFields({
  form,
  update,
  disabled,
}: {
  form: CompanyFormState;
  update: (patch: Partial<CompanyFormState>) => void;
  disabled?: boolean;
}) {
  // ANAF CUI prefill (reuses the client-form lookup, CLE-122).
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState<string | null>(null);

  async function handleAnafLookup() {
    setAnafError(null);
    const digits = form.cui.replace(/^ro\s*/i, '').replace(/\s+/g, '');
    if (!/^\d{2,10}$/.test(digits)) {
      setAnafError('Cod TVA invalid. Introdu doar cifre (ex. 47847495).');
      return;
    }
    setAnafLoading(true);
    try {
      const result = await lookupAnafByCui(form.cui);
      if (!result.found || !result.prefill) {
        setAnafError('Codul TVA nu a fost găsit în ANAF. Completează manual.');
        return;
      }
      const p = result.prefill;
      update({
        legalName: p.name || form.legalName,
        cui: p.cifCui || form.cui,
        regCom: p.regCom ?? form.regCom,
        address: p.address || form.address,
        phone: p.phone ?? form.phone,
        vatPayer: result.company?.vatPayer ?? form.vatPayer,
      });
    } catch (e) {
      setAnafError(e instanceof Error ? e.message : 'Interogarea ANAF a eșuat.');
    } finally {
      setAnafLoading(false);
    }
  }

  return (
    <>
      <div className="mb-5 border-b border-gray-100 pb-5">
        <CompanyLogoField
          value={form.logoUrl}
          onChange={(logoUrl) => update({ logoUrl })}
          disabled={disabled}
        />
      </div>

      {/* CUI + ANAF prefill */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-sm font-medium text-gray-700">
          CUI / Cod TVA
          <input
            type="text"
            value={form.cui}
            onChange={(e) => update({ cui: e.target.value })}
            disabled={disabled}
            placeholder="RO47847495"
            className="ui-input mt-1"
          />
        </label>
        <button
          type="button"
          onClick={handleAnafLookup}
          disabled={disabled || anafLoading || !form.cui.trim()}
          className="ui-btn-secondary whitespace-nowrap disabled:opacity-50"
        >
          {anafLoading ? 'Se caută…' : 'Completează de la ANAF'}
        </button>
      </div>
      {anafError && <p className="mt-2 text-sm text-red-600">{anafError}</p>}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {TEXT_FIELDS.map((f) => (
          <label
            key={f.key}
            className={`block text-sm font-medium text-gray-700 ${f.full ? 'sm:col-span-2' : ''}`}
          >
            {f.label}
            <input
              type={f.type ?? 'text'}
              value={form[f.key] as string}
              onChange={(e) => update({ [f.key]: e.target.value } as Partial<CompanyFormState>)}
              disabled={disabled}
              className="ui-input mt-1"
            />
          </label>
        ))}

        <Select
          label="Moneda implicită"
          value={form.defaultCurrency}
          onChange={(v) => update({ defaultCurrency: v as CompanyFormState['defaultCurrency'] })}
          options={[
            { value: 'RON', label: 'RON' },
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
          ]}
          disabled={disabled}
        />

        <label className="flex items-end gap-2 pb-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={form.vatPayer}
            onChange={(e) => update({ vatPayer: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300"
          />
          Plătitor de TVA
        </label>
      </div>
    </>
  );
}

/**
 * The shared company-profile form (default firm). Rendered by the Settings tab
 * and by the onboarding wizard's first step ("Compania ta").
 */
export function CompanyProfileForm({ onSaved }: { onSaved?: () => void } = {}) {
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const query = useCompanyProfile();
  const save = useSaveCompanyProfile();

  const [form, setForm] = useState<CompanyFormState>(EMPTY_COMPANY_FORM);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form from the server once (and again if we weren't touched).
  useEffect(() => {
    if (!dirty && query.data !== undefined) {
      setForm(companyToForm(query.data));
    }
  }, [query.data]);

  function update(patch: Partial<CompanyFormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setSaved(false);
  }

  async function handleSave() {
    if (!form.legalName.trim()) {
      setError('Numele legal este obligatoriu.');
      return;
    }
    setError(null);
    try {
      await save.mutateAsync(companyFormToInput(form));
      setDirty(false);
      setSaved(true);
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvarea profilului a eșuat.');
    }
  }

  const disabled = !isAdmin;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      {query.isLoading && <p className="text-sm text-gray-500">Se încarcă profilul…</p>}

      {!query.isLoading && (
        <>
          <CompanyFormFields form={form} update={update} disabled={disabled} />

          {error && (
            <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {error}
            </p>
          )}
          {saved && (
            <p className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
              Profilul companiei a fost salvat.
            </p>
          )}

          {!disabled && (
            <button
              type="button"
              onClick={handleSave}
              disabled={save.isPending}
              className="ui-btn-primary mt-4 disabled:opacity-60"
            >
              {save.isPending ? 'Se salvează…' : 'Salvează profilul'}
            </button>
          )}
          {disabled && (
            <p className="mt-4 text-xs text-gray-400">
              Doar administratorii pot modifica profilul companiei.
            </p>
          )}
        </>
      )}
    </div>
  );
}
